import { useEffect, useState } from "react";

import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Earnings = {
  grossSales: number;
  platformCommission: number;
  refunds: number;
  withdrawals: number;
  netEarnings: number;
  availableBalance: number;
  pendingBalance: number;
};

type Txn = {
  id: string;
  type: string;
  grossAmount: number;
  commissionAmount: number;
  netAmount: number;
  status: string;
  reference: string | null;
  createdAt: string;
};

export function VendorEarningsPage() {
  const [earnings, setEarnings] = useState<Earnings | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);

  useEffect(() => {
    void apiGet<Earnings>("/api/vendors/me/earnings").then((d) => setEarnings(d));
    void apiGet<{ transactions: Txn[] }>("/api/vendors/me/transactions").then((d) => setTxns(d?.transactions ?? []));
  }, []);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Earnings</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <Card label="Gross sales" value={earnings?.grossSales} />
        <Card label="Commission" value={earnings?.platformCommission} />
        <Card label="Refunds" value={earnings?.refunds} />
        <Card label="Net earnings" value={earnings?.netEarnings} />
        <Card label="Available" value={earnings?.availableBalance} />
        <Card label="Pending payout" value={earnings?.pendingBalance} />
        <Card label="Withdrawn" value={earnings?.withdrawals} />
      </div>
      <div className="amazon-card p-4">
        <h3 className="font-bold">Transactions</h3>
        <div className="overflow-x-auto">
          <table className="mt-3 w-full min-w-[500px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-1">Date</th>
                <th>Type</th>
                <th>Gross</th>
                <th>Commission</th>
                <th>Net</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 dark:border-white/10">
                  <td className="py-1.5">{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td>{t.type}</td>
                  <td>{formatPrice(t.grossAmount)}</td>
                  <td>{formatPrice(t.commissionAmount)}</td>
                  <td>{formatPrice(t.netAmount)}</td>
                  <td>{t.status}</td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-slate-500">
                    No transactions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value }: { label: string; value?: number }) {
  return (
    <div className="amazon-card p-4">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-black">{formatPrice(value ?? 0)}</p>
    </div>
  );
}
